import { useEffect, useState } from 'react';
import { api, errMsg } from '../../lib/api';

interface Hero {
  id: string;
  slug: string;
  deceasedName: string;
  birthDate: string | null;
  deathDate: string | null;
  biography: string | null;
  epitaph: string | null;
  heroCategory: string | null;
  heroEra: string | null;
  portraitKey: string | null;
  coverKey: string | null;
  gallery: string | null;
  tributeCount: number;
  status: string;
}

interface ImgVal {
  key: string;
  url: string;
}

const emptyImg: ImgVal = { key: '', url: '' };

function parseGallery(g: string | null): ImgVal[] {
  if (!g) return [];
  try {
    const arr = JSON.parse(g);
    return Array.isArray(arr) ? arr.map((k: string) => ({ key: k, url: '' })) : [];
  } catch {
    return [];
  }
}

export default function AdminHeroes() {
  const [items, setItems] = useState<Hero[]>([]);
  const [filter, setFilter] = useState('');
  const [editing, setEditing] = useState<Hero | 'new' | null>(null);
  const [notice, setNotice] = useState('');
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    const res = await api<{ items: Hero[] }>(
      `/admin/heroes${filter ? `?status=${filter}` : ''}`
    );
    if (res.ok && res.data) setItems(res.data.items);
    setLoading(false);
  }

  useEffect(() => {
    load();
  }, [filter]);

  /** 下架 / 上架：内容有问题时的紧急开关 */
  async function toggle(h: Hero) {
    const res = await api<{ status: string }>(`/admin/heroes/${h.id}/toggle`, {
      method: 'POST',
      body: '{}',
    });

    if (res.ok && res.data) {
      setNotice(`「${h.deceasedName}」已${res.data.status === 'active' ? '上架' : '下架'}`);
    } else {
      setNotice(errMsg(res, '操作失败'));
    }
    load();
  }

  const activeCount = items.filter((h) => h.status === 'active').length;

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-2">
          {['', 'active', 'archived'].map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`rounded-full px-4 py-1.5 text-xs transition ${
                filter === s ? 'bg-ink-900 text-ink-50' : 'bg-ink-100 text-ink-600'
              }`}
            >
              {s === '' ? '全部' : s === 'active' ? '已上架' : '已下架'}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <span className="text-xs text-ink-500">
            共 {items.length} 位，上架 {activeCount} 位
          </span>
          <button
            onClick={() => setEditing('new')}
            className="rounded-full bg-ink-900 px-5 py-2 text-xs text-ink-50"
          >
            + 新增英烈
          </button>
        </div>
      </div>

      {notice && (
        <p className="mb-4 rounded-lg bg-candle-300/20 px-4 py-2.5 text-sm text-ink-700">
          {notice}
        </p>
      )}

      {loading ? (
        <p className="text-sm text-ink-500">加载中…</p>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-ink-200 bg-white">
          <table className="w-full text-sm">
            <thead className="border-b border-ink-100 bg-ink-50/60 text-left text-xs text-ink-600">
              <tr>
                <th className="px-4 py-3">头像</th>
                <th className="px-4 py-3">姓名</th>
                <th className="px-4 py-3">分类</th>
                <th className="px-4 py-3">年代</th>
                <th className="px-4 py-3">生卒</th>
                <th className="px-4 py-3">祭奠</th>
                <th className="px-4 py-3">状态</th>
                <th className="px-4 py-3 text-right">操作</th>
              </tr>
            </thead>
            <tbody>
              {items.map((h) => (
                <tr key={h.id} className="border-b border-ink-50 last:border-0">
                  <td className="px-4 py-3">
                    {h.portraitKey ? (
                      <div className="h-9 w-9 overflow-hidden rounded-full bg-ink-100">
                        <img
                          src={h.portraitKey.startsWith('http') ? h.portraitKey : ''}
                          alt=""
                          className="h-full w-full object-cover"
                        />
                      </div>
                    ) : (
                      <span className="text-xs text-ink-300">无</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-ink-900">{h.deceasedName}</p>
                    {h.epitaph && (
                      <p className="mt-0.5 max-w-xs truncate text-[11px] text-ink-400">
                        {h.epitaph}
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-ink-600">{h.heroCategory ?? '—'}</td>
                  <td className="px-4 py-3 text-xs text-ink-500">{h.heroEra ?? '—'}</td>
                  <td className="px-4 py-3 text-xs text-ink-500">
                    {h.birthDate ?? '?'} — {h.deathDate ?? '?'}
                  </td>
                  <td className="px-4 py-3 text-ink-700">{h.tributeCount}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`rounded-full px-2 py-0.5 text-[10px] ${
                        h.status === 'active'
                          ? 'bg-emerald-50 text-emerald-700'
                          : 'bg-red-50 text-red-600'
                      }`}
                    >
                      {h.status === 'active' ? '已上架' : '已下架'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => setEditing(h)}
                      className="mr-3 text-xs text-ink-700 underline"
                    >
                      编辑
                    </button>
                    <button
                      onClick={() => toggle(h)}
                      className={`text-xs underline ${
                        h.status === 'active' ? 'text-red-600' : 'text-emerald-700'
                      }`}
                    >
                      {h.status === 'active' ? '下架' : '上架'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="mt-5 text-[11px] leading-relaxed text-ink-400">
        提示：下架后该条目不会在前台英雄纪念堂显示，但数据保留，可随时重新上架。
        <br />
        头像 / 封面 / 事迹配图：可直接上传，或粘贴图片链接（http 开头）。
      </p>

      {editing && (
        <HeroForm
          hero={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}
    </div>
  );
}

const INPUT =
  'h-11 w-full rounded-xl border border-ink-200 bg-ink-50/50 px-3 text-sm outline-none focus:border-ink-500 focus:bg-white';

/** 单图字段：可上传或粘贴外链 */
function ImageField({
  label,
  value,
  onChange,
  hint,
}: {
  label: string;
  value: ImgVal;
  onChange: (v: ImgVal) => void;
  hint?: string;
}) {
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState('');

  async function upload(file: File) {
    setUploading(true);
    setErr('');
    const fd = new FormData();
    fd.append('file', file);
    const res = await api<{ key: string; url: string }>('/admin/upload-image', {
      method: 'POST',
      body: fd,
    });
    setUploading(false);
    if (res.ok && res.data) onChange({ key: res.data.key, url: res.data.url });
    else setErr(errMsg(res, '上传失败'));
  }

  const preview = value.key
    ? value.url || (value.key.startsWith('http') ? value.key : '')
    : '';

  return (
    <div>
      <label className="mb-1.5 block text-xs text-ink-600">{label}</label>
      <div className="flex items-center gap-3">
        <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-lg border border-ink-200 bg-ink-50 text-xs text-ink-300">
          {preview ? <img src={preview} alt="" className="h-full w-full object-cover" /> : '无'}
        </div>
        <div className="flex flex-col gap-2">
          <label className="cursor-pointer rounded-lg border border-ink-300 px-3 py-1.5 text-xs text-ink-700">
            {uploading ? '上传中…' : '上传图片'}
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) upload(f);
                e.currentTarget.value = '';
              }}
            />
          </label>
          {value.key && (
            <button
              type="button"
              onClick={() => onChange(emptyImg)}
              className="text-[11px] text-ink-400 underline"
            >
              清除
            </button>
          )}
        </div>
      </div>
      <input
        value={value.key}
        onChange={(e) => onChange({ key: e.target.value, url: '' })}
        placeholder="或直接粘贴图片链接（http 开头）"
        className="mt-2 h-9 w-full rounded-lg border border-ink-200 bg-ink-50/50 px-2 text-xs outline-none focus:border-ink-500"
      />
      {hint && <p className="mt-1 text-[11px] text-ink-400">{hint}</p>}
      {err && <p className="mt-1 text-[11px] text-red-500">{err}</p>}
    </div>
  );
}

function HeroForm({
  hero,
  onClose,
  onSaved,
}: {
  hero: Hero | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isNew = hero === null;
  const [form, setForm] = useState({
    deceasedName: hero?.deceasedName ?? '',
    birthDate: hero?.birthDate ?? '',
    deathDate: hero?.deathDate ?? '',
    heroCategory: hero?.heroCategory ?? '古代民族英雄',
    heroEra: hero?.heroEra ?? '',
    epitaph: hero?.epitaph ?? '',
    biography: hero?.biography ?? '',
    status: hero?.status ?? 'active',
  });
  const [portrait, setPortrait] = useState<ImgVal>({ key: hero?.portraitKey ?? '', url: '' });
  const [cover, setCover] = useState<ImgVal>({ key: hero?.coverKey ?? '', url: '' });
  const [gallery, setGallery] = useState<ImgVal[]>(parseGallery(hero?.gallery ?? null));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  function set<K extends keyof typeof form>(k: K, v: string) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError('');

    const payload = {
      ...form,
      portraitKey: portrait.key || undefined,
      coverKey: cover.key || undefined,
      gallery: gallery.filter((g) => g.key).length
        ? JSON.stringify(gallery.map((g) => g.key))
        : undefined,
    };

    const res = isNew
      ? await api('/admin/heroes', { method: 'POST', body: JSON.stringify(payload) })
      : await api(`/admin/heroes/${hero!.id}`, {
          method: 'PATCH',
          body: JSON.stringify(payload),
        });

    setSaving(false);
    if (res.ok) onSaved();
    else setError(errMsg(res, '保存失败'));
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-6"
      onClick={onClose}
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl rounded-3xl bg-white p-6"
      >
        <h3 className="mb-5 font-serifcn text-lg text-ink-900">
          {isNew ? '新增英烈' : `编辑：${hero!.deceasedName}`}
        </h3>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1.5 block text-xs text-ink-600">
              姓名<span className="ml-1 text-red-500">*</span>
            </label>
            <input
              value={form.deceasedName}
              onChange={(e) => set('deceasedName', e.target.value)}
              className={INPUT}
              required
            />
          </div>

          <div>
            <label className="mb-1.5 block text-xs text-ink-600">所属年代</label>
            <input
              value={form.heroEra}
              onChange={(e) => set('heroEra', e.target.value)}
              placeholder="如：南宋 / 抗日战争时期"
              className={INPUT}
            />
          </div>

          <div>
            <label className="mb-1.5 block text-xs text-ink-600">出生年份</label>
            <input
              value={form.birthDate}
              onChange={(e) => set('birthDate', e.target.value)}
              placeholder="如：1103"
              className={INPUT}
            />
          </div>

          <div>
            <label className="mb-1.5 block text-xs text-ink-600">牺牲 / 逝世年份</label>
            <input
              value={form.deathDate}
              onChange={(e) => set('deathDate', e.target.value)}
              placeholder="如：1142"
              className={INPUT}
            />
          </div>

          <div className="sm:col-span-2">
            <label className="mb-1.5 block text-xs text-ink-600">分类</label>
            <select
              value={form.heroCategory}
              onChange={(e) => set('heroCategory', e.target.value)}
              className={INPUT}
            >
              {['古代民族英雄', '近代先烈', '抗战英烈', '抗美援朝英烈', '历史英雄'].map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>

          <div className="sm:col-span-2">
            <label className="mb-1.5 block text-xs text-ink-600">墓志铭（一句话）</label>
            <input
              value={form.epitaph}
              onChange={(e) => set('epitaph', e.target.value)}
              className={INPUT}
            />
          </div>

          {/* 头像 / 封面 / 事迹配图 */}
          <div className="sm:col-span-1">
            <ImageField label="头像" value={portrait} onChange={setPortrait} />
          </div>
          <div className="sm:col-span-1">
            <ImageField label="封面图" value={cover} onChange={setCover} />
          </div>

          <div className="sm:col-span-2">
            <div className="mb-1.5 flex items-center justify-between">
              <span className="text-xs text-ink-600">生平事迹配图</span>
              <button
                type="button"
                onClick={() => setGallery((g) => [...g, emptyImg])}
                className="text-[11px] text-ink-700 underline"
              >
                + 添加一张
              </button>
            </div>
            {gallery.length === 0 && (
              <p className="text-[11px] text-ink-400">暂无配图，可逐张上传或粘贴链接</p>
            )}
            <div className="space-y-2">
              {gallery.map((g, i) => (
                <div key={i} className="flex items-center gap-2">
                  <div className="h-10 w-10 flex-shrink-0 overflow-hidden rounded border border-ink-200 bg-ink-50">
                    {(g.url || (g.key.startsWith('http') ? g.key : '')) && (
                      <img
                        src={g.url || (g.key.startsWith('http') ? g.key : '')}
                        alt=""
                        className="h-full w-full object-cover"
                      />
                    )}
                  </div>
                  <input
                    value={g.key}
                    onChange={(e) =>
                      setGallery((arr) =>
                        arr.map((x, idx) => (idx === i ? { key: e.target.value, url: '' } : x))
                      )
                    }
                    placeholder="图片链接（http 开头）或上传后自动填入"
                    className="h-9 flex-1 rounded-lg border border-ink-200 bg-ink-50/50 px-2 text-xs outline-none focus:border-ink-500"
                  />
                  <label className="cursor-pointer rounded-lg border border-ink-300 px-2 py-1.5 text-[11px] text-ink-700">
                    上传
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={async (e) => {
                        const f = e.target.files?.[0];
                        if (!f) return;
                        const fd = new FormData();
                        fd.append('file', f);
                        const res = await api<{ key: string; url: string }>('/admin/upload-image', {
                          method: 'POST',
                          body: fd,
                        });
                        if (res.ok && res.data) {
                          const { key, url } = res.data;
                          setGallery((arr) =>
                            arr.map((x, idx) => (idx === i ? { key, url } : x))
                          );
                        }
                        e.currentTarget.value = '';
                      }}
                    />
                  </label>
                  <button
                    type="button"
                    onClick={() => setGallery((arr) => arr.filter((_, idx) => idx !== i))}
                    className="text-[11px] text-red-500 underline"
                  >
                    删
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="sm:col-span-2">
            <label className="mb-1.5 block text-xs text-ink-600">生平事迹</label>
            <textarea
              value={form.biography}
              onChange={(e) => set('biography', e.target.value)}
              rows={5}
              placeholder="客观陈述生卒、身份与主要事迹，避免主观评价"
              className="w-full rounded-xl border border-ink-200 bg-ink-50/50 p-3 text-sm outline-none focus:border-ink-500 focus:bg-white"
            />
          </div>

          <div className="sm:col-span-2">
            <label className="mb-1.5 block text-xs text-ink-600">状态</label>
            <select
              value={form.status}
              onChange={(e) => set('status', e.target.value)}
              className={INPUT}
            >
              <option value="active">上架（前台可见）</option>
              <option value="archived">下架（前台隐藏）</option>
            </select>
          </div>
        </div>

        {error && (
          <p className="mt-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">{error}</p>
        )}

        <div className="mt-6 flex gap-3">
          <button
            type="submit"
            disabled={saving}
            className="h-11 flex-1 rounded-xl bg-ink-900 text-sm text-ink-50 disabled:opacity-50"
          >
            {saving ? '保存中…' : '保存'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="h-11 rounded-xl border border-ink-200 px-6 text-sm text-ink-600"
          >
            取消
          </button>
        </div>
      </form>
    </div>
  );
}
